import { Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { AdminShell } from "@project/admin/components/admin-shell";
import { Input } from "@project/components/ui/input";
import { Badge } from "@project/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@project/components/ui/table";
import { Search, Eye } from "lucide-react";
import { mockResidents } from "@project/lib/mock-modules";

function healthBadge(h: string) {
  if (h === "Stable") {
    return (
      <Badge className="bg-emerald-500/15 text-emerald-700 hover:bg-emerald-500/15">
        Stable
      </Badge>
    );
  }
  if (h === "Monitoring") {
    return (
      <Badge className="bg-amber-500/15 text-amber-700 hover:bg-amber-500/15">
        Monitoring
      </Badge>
    );
  }
  return (
    <Badge className="bg-rose-500/15 text-rose-700 hover:bg-rose-500/15">
      Critical
    </Badge>
  );
}

export default function ResidentsDirectory() {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) {
      return mockResidents;
    }
    return mockResidents.filter(r =>
      [r.name, r.id, r.room, r.bloodGroup].some(v =>
        v.toLowerCase().includes(s)
      )
    );
  }, [q]);

  return (
    <AdminShell
      title="Residents Directory"
      subtitle="Browse all residents in the home"
    >
      <div className="mb-4 flex items-center gap-2">
        <div className="relative max-w-sm flex-1">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={q}
            onChange={e => setQ(e.target.value)}
            placeholder="Search by name, ID, room…"
            className="pl-8"
          />
        </div>
        <div className="text-xs text-muted-foreground">
          {filtered.length} of {mockResidents.length}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-card shadow-sm">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>ID</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Age / Gender</TableHead>
              <TableHead>Room</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Health</TableHead>
              <TableHead>Blood</TableHead>
              <TableHead className="text-right">View</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.map(r => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.id}</TableCell>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell>
                  {r.age} · {r.gender}
                </TableCell>
                <TableCell>{r.room}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{r.status}</Badge>
                </TableCell>
                <TableCell>{healthBadge(r.health)}</TableCell>
                <TableCell>{r.bloodGroup}</TableCell>
                <TableCell className="text-right">
                  <Link
                    to="/admin/elder-care/residents/$id"
                    params={{ id: r.id }}
                    className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  >
                    <Eye className="h-3.5 w-3.5" /> View
                  </Link>
                </TableCell>
              </TableRow>
            ))}
            {filtered.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={8}
                  className="py-10 text-center text-sm text-muted-foreground"
                >
                  No residents match your search.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </AdminShell>
  );
}
